// Hopefully temporary separate file for when simple answer to sharing types without having dependency errors appears by magic

import { AdjacentType } from './common'
import { MapLike, ErrData } from './core'
import { FilterGroup, isFilterGroup } from './graphQueryTypes'
import { QueryCommandOutput, ScanCommandOutput } from '@aws-sdk/lib-dynamodb'
import { NativeAttributeValue } from '@aws-sdk/util-dynamodb'

export type DocumentClientKey = Record<string, NativeAttributeValue>
export type DocumentClientAttributeMap = DocumentClientKey
export type DocumentClientItemList = DocumentClientAttributeMap[]

export enum PathDirection {
  parent = 'PARENT',
  child = 'CHILD',
}

export enum ConditionTypes {
  between = 'BETWEEN',
  beginsWIth = 'BEGINS_WITH',
  contains = 'CONTAINS',
  equals = '=',
  notEquals = '<>',
  and = 'and',
}

export enum FilterTypes {
  simpleAnd = 'SIMPLE_AND',
}

export const PSEUDO_ATTRIBUTE_TYPEIDS: string[] = [
  'DeviceConfig',
  'DeviceHistory',
  'DeviceControlTransaction',
  'Incident',
  'ObjectHistory',
  'ObservationHistory',
  'ThingShadowState'
]


export type ValueTypes = boolean | ValueTypes[] | { [k: string]: ValueTypes } | number | string | string[] | null | undefined | { hi: string | number; lo: string | number };

export type FilterCondition = { condition: ConditionTypes; key: string; value: ValueTypes }

export type FilterType = {
  conditions?: FilterCondition[]
  filterType?: FilterTypes

  // early implmentation, just type type id
  type?: string
  // early implementation, just an sk
  sk?: string
}

export enum QueryTypes {
  simplePath = 'SIMPLE_PATH', // [{type: sometype}] currently descends
  directedPath = 'DIRECTED_PATH', // [{type: sometype, direction: [PARENT/CHILD]]}
  filteredGet = 'FILTERED_GET',
  searchByType = 'SEARCH_BY_TYPE',
  LIST_LIMITED_ALL_TYPES = 'LIST_LIMITED_ALL_TYPES',
}

export type S3ItemReference = {
  bucketIdentifier: string
  key: string
  Etag?: string
}

export type dbObjectMeta = {
  s3?: S3ItemReference
  lastUpdatedMs?: number
  /** a flag indicating this item is not user deletable */
  protected?: true
}

export const hasMetadata = <T = any>(x: T): x is T & { metadata: dbObjectMeta } => !!x && typeof (x as T & { metadata: dbObjectMeta }).metadata === 'object'
export const hasS3Metadata = <T = any>(x: any): x is T & { metadata: dbObjectMeta & { s3: S3ItemReference } } =>
  hasMetadata(x) && x.metadata.s3?.bucketIdentifier && x.metadata.s3?.key

/** adds in metadata M to type D and extends D.metadata if present */
export type ExtendedMetadata<D, M> =
  M extends Record<string, any> ? (D extends { metadata?: unknown } ? D & { metadata: M & D['metadata'] } : D & { metadata: M }) : D

export type AdditionalMetaData<T extends MapLike<any>> = { metadata?: dbObjectMeta & T }

export type IdType = { id: string }
export type MayContainMetaData = { metadata?: dbObjectMeta }
export type PotentialObjectType = { type: string } & IdType
export type DatastoreObjectType = { sk: string; pk: string }
export type DatastoreObjectTypes = DatastoreObjectType[]
export type OptionalShortName = { shortName?: string }
export type CoreApplicationObjectType = DatastoreObjectType & PotentialObjectType & MayContainMetaData & OptionalShortName
export type NamedApplicationObjectType = CoreApplicationObjectType & { name: string }
export interface ValidBusinessObject extends MapLike<any>, PotentialObjectType, MayContainMetaData, DatastoreObjectType {}
export type ValidBusinessObjectList = ValidBusinessObject[]
export type PossibleBusinessObject = Partial<DatastoreObjectType> & { [k: string]: any; type: string; id?: string }
export type CommonBusinessObject = Partial<DatastoreObjectType> & { [k: string]: any; type?: string; id?: string }
export type TypeScopedValidCoreApplicationType<T extends string> = CoreApplicationObjectType & { type: T }
export type EdgeInstanceType<ET = string, Atts = MapLike<any>> = CoreApplicationObjectType & { edgeTypeId: ET; edgeAttributes?: Atts }
export type NodeInstanceType = CoreApplicationObjectType & { edgeTypeId: never; edgeAttributes?: never }

/** Direction of Graph Traversal in the canonical sense of the datatmodel as defined in the EdgeTypeMatrix */
export enum Traversal {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
}

export const TraversalToAdjacentType = {
  [Traversal.ASCENDING]: AdjacentType.PARENT,
  [Traversal.DESCENDING]: AdjacentType.CHILD,
}

/** contraint of edgeType */
export enum Cardinality {
  NONE = 'NONE',
  ONE = 'ONE',
  MANY = 'MANY',
}

export const CardinalityMap: Record<Cardinality, number | null> = {
  MANY: null,
  ONE: 1,
  NONE: 0,
}

export const getCardinality = (cardinalityNumber: number | null): Cardinality => {
  let cardinality: Cardinality = Cardinality.NONE
  if (cardinalityNumber === null) {
    cardinality = Cardinality.MANY
  } else if (cardinalityNumber === 0) {
    cardinality = Cardinality.NONE
  } else if (cardinalityNumber === 1) {
    cardinality = Cardinality.ONE
  } else if (cardinalityNumber > 1) {
    cardinality = Cardinality.MANY
  }
  return cardinality
}

export interface DynamicIndexObjectType {
  dsk?: string
  dpk?: string
}

export type NakedEdge = {
  sk: string
  pk: string
  id: string
  type: string
  ttl?: number
  name?: string
  edgeTypeId?: string
  edgeAttributes?: MapLike<any>
}

export type DatastoreKey = string
export const isDatastoreKey = (ob: any): ob is DatastoreKey => {
  let isKey = false
  if (typeof ob === 'string') {
    const parts = ob.split(':')
    const partsValid = parts.every((p) => {
      const valid = /^[a-zA-Z0-9:-]+$/.test(p)
      return valid
    })
    isKey = parts.length > 1 && /^[a-zA-Z]+$/.test(parts[0]) && partsValid
  }
  return isKey
}

export const isDatastoreObject = (item: any): item is DatastoreObjectType => !!item && isDatastoreKey(item.sk) && isDatastoreKey(item.pk)

export const isEdge = (item: any): item is EdgeInstanceType => isDatastoreObject(item) && (item.sk !== item.pk)

export const isNode = (item: any): item is NodeInstanceType => isDatastoreObject(item) && item.sk === item.pk && !(item as EdgeInstanceType).edgeTypeId

// where data has been leaked from edges onto nodes. Used where such bad data would throw part of a long iterative process and its deemed acceptable
export const isMalformedNode = ( item: any ): item is NodeInstanceType => isDatastoreObject( item ) && item.sk === item.pk && (
  !!( item as ValidBusinessObject).edgeTypeId
)

export const isNakedEdge = (item: DatastoreObjectType): item is NakedEdge => {
  let isNaked: boolean = false
  const validKeys: (keyof NakedEdge)[] = ['type', 'id', 'pk', 'sk', 'name']
  isNaked = isEdge(item) && Object.keys(item).every((itemKey) => validKeys.includes(itemKey as keyof NakedEdge))
  return isNaked
}

export const isPseudoAttribute = ( vob: DatastoreObjectType ) => isDatastoreKey(vob.pk) && PSEUDO_ATTRIBUTE_TYPEIDS.includes( ( vob as CoreApplicationObjectType).type )


/** removes all non edge attributes. Be careful when using getObjectHash to compare for changes as ttl is added as undefined and will change hash of a raw edge from dynamodb */
export const stripEdge = (item: DocumentClientAttributeMap | ValidBusinessObject): NakedEdge =>
  (({ sk, pk, id, type, ttl, name, edgeTypeId, edgeAttributes }) => ({ sk, pk, id, type, ttl, name, edgeTypeId, edgeAttributes }))(item as ValidBusinessObject)

/** returns base ValidObjectType keys plus any defined in preservedKeys */
export const stripNode = (item: DocumentClientAttributeMap | ValidBusinessObject, presevedKeys?: string[]): ValidBusinessObject => {
  const datastoreObject = (({ sk, pk, id, type, name }) => ({ sk, pk, id, type, name }))(item as ValidBusinessObject)
  const others = presevedKeys ? Object.fromEntries(presevedKeys.map((k) => [k, item[k]])) : {}
  Object.assign(datastoreObject, others)
  return datastoreObject
}

export const isEdgeForNode = (edge: DatastoreObjectType, node: DatastoreObjectType) => {
  let isEdgeForNode = isEdge(edge) && isNode(node)
  const matchedByPk = edge.pk === node.sk
  const matchedBySk = edge.sk === node.sk
  isEdgeForNode = isEdgeForNode && (matchedByPk || matchedBySk)
  return isEdgeForNode
}

/** matches objects on pk and sk */
export const identitiesMatch = (a: any, b: any) => isDatastoreObjectType(a) && isDatastoreObjectType(b) && a.sk == b.sk && a.pk == b.pk

export const getZombieInstanceFromPrimaryKey = <T extends MapLike<any>>(pk: string, others?: T): ValidBusinessObject & T => {
  const [type, ...idOthers] = pk.split(':')
  const id = idOthers.join(':')
  const zombieInstance = { type, id, sk: `${type}:${id}`, pk: `${type}:${id}` }

  return { ...zombieInstance, ...others } as ValidBusinessObject & T
}

export const getZombieInstanceFromDatastoreObject = ( dob: DatastoreObjectType ) => getZombieInstanceFromPrimaryKey( dob.sk, dob)

export const getPredictableZombie = (type: string, id: number = 0, others?: MapLike<any>): ValidBusinessObject => {
  const UUIDCompliant = `00000000-0000-5000-A000-${id.toString().padStart(12, '0')}`
  const sk = `${type}:${UUIDCompliant}`

  return getZombieInstanceFromPrimaryKey(sk, { name: `${type} ${UUIDCompliant}`, id: UUIDCompliant, ...others })!
}

export const getPredictableZombies = (type: string, numberOfZombies: number, others?: MapLike<any>): ValidBusinessObjectList => {
  let zombies: ValidBusinessObjectList = []
  for (let i = 0; i < numberOfZombies; i++) {
    zombies.push(getPredictableZombie(type, i, others))
  }

  return zombies
}

export const getZombieEdge = (source: ValidBusinessObject, target: ValidBusinessObject, edgeTypeId?: string, edgeAttributes?: Record<string, any>) => {
  const edge = stripEdge({ ...target, pk: source.pk, edgeTypeId })
  edge.edgeAttributes = edgeAttributes
  if (edge.ttl === undefined) {
    delete edge.ttl
  }
  return edge
}

export type PathStepType = {
  type: QueryTypes
  filter: FilterType
  direction: PathDirection
}

export type TraverserStepType = {
  type: string
  direction: PathDirection
}

export type RemovedItemResult = {
  err: Error | null
  data: {
    referenceItem: ValidBusinessObject | undefined
    removedItem: ValidBusinessObject | undefined
  } | null
}

export type DeleteItemResult = ValidBusinessObject | undefined

export const isDatastoreObjectType = (x: any): x is DatastoreObjectType => {
  const c = x as DatastoreObjectType
  return typeof c.pk === 'string' && typeof c.sk === 'string'
}

export const isValidBusinessObject = (item: undefined | null | { [k: string]: any }): item is ValidBusinessObject => {
  if (!item) return false
  const possible = item as ValidBusinessObject
  return possible.type !== undefined && possible.id !== undefined && possible.sk !== undefined && possible.pk !== undefined
}

export const isVOBofType = <T extends string>(type: T, item: undefined | { [k: string]: any }): item is TypeScopedValidCoreApplicationType<T> => {
  if (!item) return false
  const possible = item as TypeScopedValidCoreApplicationType<T>
  return possible.type === type && isValidBusinessObject(item)
}

export const isVOBArrayOfSameType = (arr: any[]): arr is ValidBusinessObjectList => {
  return (
    arr.every(isValidBusinessObject) &&
    arr.reduce((a, c, i, arr) => {
      const next = arr[i + 1]
      if (next) {
        a = c.type === next.type
      }
      return a
    }, true as boolean)
  )
}

/** produces unique array where array input may have dups, use repalceMatches where dups may have been pushed onto array and they should overwrite existings */
export const ValidObjectDeduper = <T extends DatastoreObjectType>(obs: T[], replaceMatches?: boolean) =>
  obs.reduce((a, c) => {
    const existsIdx = a.findIndex((ai) => identitiesMatch(ai, c))
    if (existsIdx === -1) {
      a.push(c)
    } else if (replaceMatches === true) {
      a.splice(existsIdx, 1, c)
    }
    return a
  }, [] as T[])

/** defines a graph step between one or more context objects ( external ), via an optionally edgeTyped edge, in a direction, with target of type objectTypeId
 * optionally can have nodeFilters: FilterGroups[] which SOME BUT NOT ALL consumers will apply to result node list in the step
 *
 * Non node filtering
 * consumerAPI deviceIsAllocatableThroughHierarchy
 * system-provisioing allocateViaHierarchy and other methods
 * device-controller lambda allowDeviceControlByProperty and other methods
 * datastore: getTypeHierarchy
 * PseudoStore: getAdjacentEdges
 *
 * Node filtering
 * datastore: getGraphTraversal
 * NodeEdgeMapper class getAdjacentEdges, getAdjacentNodesAndEdges (Aug 2023)
 */
export type EdgeParams = {
  objectTypeId: string
  adjacencyType?: AdjacentType.CHILD | AdjacentType.PARENT | AdjacentType.SUBJECT
  edgeTypeId?: string
  nodeFilters?: FilterGroup[]
}

export const isEdgeParams = (x: any): x is EdgeParams => {
  const y = x as EdgeParams
  const hasObjectType = !!y.objectTypeId
  const mayHaveAdjacencyType = !y.adjacencyType || [AdjacentType.CHILD, AdjacentType.PARENT, AdjacentType.SUBJECT].includes(y.adjacencyType)
  const mayHaveEdgeTypeId = !y.edgeTypeId || typeof y.edgeTypeId === 'string'
  const mayHaveNodeFilters = !y.nodeFilters || y.nodeFilters.every(isFilterGroup)
  return hasObjectType && mayHaveAdjacencyType && mayHaveEdgeTypeId && mayHaveNodeFilters
}

export type TypeHierarchyRequest = {
  apexNode: DocumentClientAttributeMap & CoreApplicationObjectType & DatastoreObjectType
  edgeParams: EdgeParams
  maxIterations: number | undefined
}

export type NodeCollectionTypeHierarchyRequest = {
  nodes: (DocumentClientAttributeMap & CoreApplicationObjectType & DatastoreObjectType)[]
  edgeParams: EdgeParams
  maxIterations: number | undefined
}

export type PathStepResult<T extends DocumentClientAttributeMap> = {
  edges: T[]
  nodes: T[]
  pseudoAttributes: T[] | null
}


export type TypeHierarchyResponse = PathStepResult<DatastoreObjectType>

export type QueryPathResult = PathStepResult<ValidBusinessObject>

export type GetGetGraphTraversalRequest = {
  contextObjects: DatastoreObjectTypes
  path: EdgeParams[]
}

export type EdgeTravesalPath = EdgeParams[]
export const isValidEdgeTraversalPath = (item: any): item is EdgeTravesalPath => item instanceof Array && item.every(isEdgeParams)

export const isGetGraphTraversalRequest = (request: any): request is GetGetGraphTraversalRequest => {
  const { contextObjects, path } = request as GetGetGraphTraversalRequest
  return isValidEdgeTraversalPath(path) && contextObjects.every(isDatastoreObject)
}
/** output short pk to sk version of object as string, for development */
export const summariseDatastoreObjectKeys = <T extends DatastoreObjectType>(ob: T) => `${ob.pk} > ${ob.sk}`

export type GetGetGraphTraversalResponse = { lastNodesAndEdges: TypeHierarchyResponse; hierarchy: TypeHierarchyResponse }

export type ObjectHashhMapType = { [sk: string]: { [pk: string]: string } }

export type SkMappedItems<T extends DatastoreObjectType = DatastoreObjectType> = { [sk: string]: { [pk: string]: T | undefined } }
export type PKMappedItems<T extends DatastoreObjectType = DatastoreObjectType> = { [pk: string]: { [sk: string]: T | undefined } }

/** To retain object references NodeEdgeMapper constructor uses SKMapped to construct PKMapped rather then raw provided item */
export type HydrateableSkMappedItems<T extends DatastoreObjectType = DatastoreObjectType> = {
  skMapped: SkMappedItems<T>
}

export interface IEdgeMappedItems<T extends DatastoreObjectType> {
  skMapped: SkMappedItems<T>
  pkMapped: PKMappedItems<T>
}

export type NodeEdgemapperLike<T extends DatastoreObjectType> = {
  nodeCount: number
  edgeCount: number
  queryHashes?: string[]

  axisNode: T | undefined
} & IEdgeMappedItems<T> &
  OptionalQueryHashes

export interface OptionalQueryHashes {
  queryHashes?: string[]
}

export type QueryCommandOutputNoMeta = Omit<QueryCommandOutput, '$metadata'>

export type ScanCommandOutputNoMeta = Omit<ScanCommandOutput, '$metadata'>

const queryDataMerge = (existingQueryOutput: QueryCommandOutputNoMeta | null, nextQueryOutput: QueryCommandOutputNoMeta | null) => {
  if (nextQueryOutput) {
    if (!existingQueryOutput) {
      existingQueryOutput = {}
    }
    if (nextQueryOutput.Items) {
      existingQueryOutput.Items = existingQueryOutput.Items ? [...existingQueryOutput.Items, ...nextQueryOutput.Items] : nextQueryOutput.Items
    }
    if (nextQueryOutput.Count) {
      existingQueryOutput.Count = existingQueryOutput.Count ? existingQueryOutput.Count + nextQueryOutput.Count : nextQueryOutput.Count
    }
    if (nextQueryOutput.LastEvaluatedKey) {
      existingQueryOutput = nextQueryOutput.LastEvaluatedKey
    }
  }
  return existingQueryOutput
}

/** aggregates the ErrData output of an array of queries */
export const ErrDataQueryOutputReducer = (results: ErrData<QueryCommandOutputNoMeta>[]): ErrData<QueryCommandOutputNoMeta> =>
  results.reduce(
    (acc, curr) => {
      const mutant: ErrData<QueryCommandOutputNoMeta> = {
        err: curr.err ? (acc.err ? new Error(acc.err.message + '\n' + curr.err?.message) : new Error(curr.err?.message)) : acc.err,
        data: queryDataMerge(acc.data, curr.data),
      }
      return mutant
    },
    { err: null, data: null } as ErrData<QueryCommandOutputNoMeta>,
  )
